
import type { OdooSession, OdooJsonRpcResponse, OdooAuthParams } from '@/types';
import type { ImportMappingTemplate } from '@/types';
import { appConfig } from '@/config/app-config';

let rpcId = 0;

/** Rate limiter for login attempts - prevents brute force */
const loginAttempts: { count: number; lastAttempt: number } = { count: 0, lastAttempt: 0 };
const MAX_LOGIN_ATTEMPTS = 5;
const LOGIN_COOLDOWN_MS = 60_000; // 1 minute

export class OdooClient {
  private apiUrl: string;
  private url: string = '';
  private db: string = '';
  private sessionId: string = '';
  private uid: number | null = null;
  private sessionStartTime: number = 0;

  // ─── Performance: In-memory cache ───
  private cache = new Map<string, { data: any; expiry: number }>();
  private defaultCacheTTL = 30_000; // 30 seconds

  // ─── Stability: Request queue & dedup ───
  private pendingRequests = new Map<string, Promise<any>>();
  private requestQueue: Array<() => void> = [];
  private activeRequests = 0;
  private maxConcurrent = 6; // Max parallel requests to Odoo

  // ─── Stability: Retry config ───
  private maxRetries = 2;
  private retryDelayMs = 1000;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl || appConfig.backendUrl;
    // Restore saved URL and DB from AppConfig
    this.url = appConfig.odooUrl;
    this.db = appConfig.odooDb;
  }

  /** Get cached data if still valid */
  private getCached(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  /** Store data in cache with TTL */
  private setCache(key: string, data: any, ttlMs?: number): void {
    this.cache.set(key, { data, expiry: Date.now() + (ttlMs || this.defaultCacheTTL) });
  }

  /** Generate cache key from model, domain, fields, limit, offset, order */
  private cacheKey(model: string, domain: any[], fields: string[], limit?: number, offset?: number, order?: string): string {
    return `${model}:${JSON.stringify(domain)}:${fields.join(',')}:${limit || 0}:${offset || 0}:${order || ''}`;
  }

  /** Invalidate cache for a specific model */
  invalidateModelCache(model: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${model}:`)) {
        this.cache.delete(key);
      }
    }
  }

  /** Clear all cache */
  clearAllCache(): void {
    this.cache.clear();
  }

  /** Concurrency-limited RPC with automatic retry and dedup */
  private async queuedRpc(path: string, params: Record<string, any>, retries = 0): Promise<any> {
    // Dedup: if same request is already in-flight, reuse it
    const dedupKey = `${path}:${JSON.stringify(params)}`;
    const pending = this.pendingRequests.get(dedupKey);
    if (pending) return pending;

    const executeWithQueue = async (): Promise<any> => {
      // Wait for available slot
      if (this.activeRequests >= this.maxConcurrent) {
        await new Promise<void>(resolve => this.requestQueue.push(resolve));
      }
      this.activeRequests++;

      try {
        const result = await this.rpc(path, params);
        return result;
      } catch (error: any) {
        // Retry on network errors or server 5xx
        const isRetryable = error?.message?.includes('fetch') ||
          error?.message?.includes('502') ||
          error?.message?.includes('503') ||
          error?.message?.includes('504') ||
          error?.message?.includes('network') ||
          error?.message?.includes('الاتصال');

        if (isRetryable && retries < this.maxRetries) {
          const delay = this.retryDelayMs * (retries + 1);
          console.warn(`[OdooClient] Retrying (${retries + 1}/${this.maxRetries}) after ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          return this.queuedRpc(path, params, retries + 1);
        }
        throw error;
      } finally {
        this.activeRequests--;
        // Process next queued request
        const next = this.requestQueue.shift();
        if (next) next();
      }
    };

    const promise = executeWithQueue();
    this.pendingRequests.set(dedupKey, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      this.pendingRequests.delete(dedupKey);
    }
  }

  get isConnected(): boolean {
    return !!this.sessionId && !!this.uid;
  }

  get currentUid(): number | null {
    return this.uid;
  }

  get currentDb(): string {
    return this.db;
  }

  get currentUrl(): string {
    return this.url;
  }

  /** Check if session has timed out based on server-configured timeout */
  get isSessionExpired(): boolean {
    if (!this.sessionStartTime) return false;
    return Date.now() - this.sessionStartTime > appConfig.sessionTimeout;
  }

  /**
   * Authenticate with Odoo via Worker proxy
   * Includes rate limiting to prevent brute force attacks
   */
  async authenticate(params: OdooAuthParams): Promise<OdooSession> {
    // Rate limiting check - prevent brute force attacks
    const now = Date.now();
    if (loginAttempts.count >= MAX_LOGIN_ATTEMPTS) {
      const elapsed = now - loginAttempts.lastAttempt;
      if (elapsed < LOGIN_COOLDOWN_MS) {
        const remaining = Math.ceil((LOGIN_COOLDOWN_MS - elapsed) / 1000);
        throw new Error(`تم تجاوز الحد المسموح من المحاولات. حاول مرة أخرى بعد ${remaining} ثانية`);
      }
      loginAttempts.count = 0;
    }

    this.url = params.url.replace(/\/+$/, '');
    this.db = params.db;

    // URL and DB are now managed by Cloudflare env vars via /config endpoint
    // No need to write back to AppConfig (read-only from runtime config)

    const response = await this.rpc('/web/session/authenticate', {
      db: params.db,
      login: params.login,
      password: params.password,
    });

    if (response.error) {
      // Increment rate limiter on failure
      loginAttempts.count++;
      loginAttempts.lastAttempt = Date.now();
      const msg = response.error.data?.message || response.error.message || 'فشل المصادقة';
      throw new Error(msg);
    }

    // Reset rate limiter on successful login
    loginAttempts.count = 0;
    loginAttempts.lastAttempt = 0;

    const result = response.result;
    this.sessionId = response._sessionId || result.session_id || '';
    this.uid = result.uid;
    this.sessionStartTime = Date.now();

    const session: OdooSession = {
      session_id: this.sessionId,
      uid: this.uid!,
      name: result.name || '',
      username: result.username || params.login,
      partner_id: result.partner_id || 0,
      db: this.db,
      email: result.email || '',
      phone: result.phone || '',
    };

    this.saveSession(session);
    this.saveCredentials({ url: this.url, db: this.db });

    return session;
  }

  /**
   * Lookup user login (email) by phone number via Worker proxy.
   * The Worker uses server-side service account credentials (never exposed to client).
   * Env vars needed on Worker: PHONE_LOOKUP_USER, PHONE_LOOKUP_PASSWORD, ODOO_URL, ODOO_DB
   */
  async lookupLoginByPhone(phone: string): Promise<string | null> {
    try {
      const baseUrl = appConfig.baseUrl;
      const res = await fetch(`${baseUrl}/auth/phone-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });

      if (!res.ok) {
        console.warn('Phone lookup request failed:', res.status);
        return null;
      }

      const data = await res.json();
      return data.login || null;
    } catch (e) {
      console.error('Phone lookup failed:', e);
      return null;
    }
  }

  /**
   * Restore session from browser storage
   */
  async restoreSession(): Promise<boolean> {
    const saved = this.loadSession();
    if (!saved) return false;

    const credentials = this.loadCredentials();
    if (!credentials) return false;

    // Check session timeout from server config
    const savedTime = localStorage.getItem('odoo_session_time');
    if (savedTime) {
      const elapsed = Date.now() - parseInt(savedTime);
      if (elapsed > appConfig.sessionTimeout) {
        this.clearSession();
        return false;
      }
    }

    this.sessionId = saved.session_id;
    this.uid = saved.uid;
    this.url = credentials.url;
    this.db = credentials.db;
    this.sessionStartTime = savedTime ? parseInt(savedTime) : Date.now();

    try {
      await this.searchRead('res.users', [['id', '=', this.uid!]], ['id'], 1);
      return true;
    } catch {
      this.clearSession();
      return false;
    }
  }

  /**
   * Search and read records - with caching and concurrency control
   */
  async searchRead(
    model: string,
    domain: any[],
    fields: string[],
    limit?: number,
    offset?: number,
    order?: string
  ): Promise<any[]> {
    // Check cache first
    const key = this.cacheKey(model, domain, fields, limit, offset, order);
    const cached = this.getCached(key);
    if (cached) return cached;

    const response = await this.queuedRpc('/web/dataset/call_kw', {
      model,
      method: 'search_read',
      args: [domain],
      kwargs: {
        fields,
        limit: limit || 80,
        offset: offset || 0,
        order: order || 'id desc',
      },
    });

    if (response.error) {
      throw new Error(response.error.data?.message || 'Search read failed');
    }

    const result = response.result || [];
    // Cache the result (reference data gets longer TTL)
    const ttl = this.isReferenceModel(model) ? 120_000 : this.defaultCacheTTL;
    this.setCache(key, result, ttl);
    return result;
  }

  /** Check if model is reference/lookup data (rarely changes) */
  private isReferenceModel(model: string): boolean {
    return ['res.company', 'res.country', 'res.country.state', 'x_res.country.state.city',
      'x_brand', 'x_service_type', 'x_product_symptoms', 'x_product.category',
      'x_employee_performance', 'account.journal', 'res.users'].includes(model);
  }

  // ─── Model field discovery cache ───
  private modelFieldsCache = new Map<string, Set<string>>();

  /**
   * Get valid field names for a model from Odoo using fields_get.
   * Results are cached for the session to avoid repeated calls.
   */
  async getValidFields(model: string, requestedFields: string[]): Promise<string[]> {
    const cached = this.modelFieldsCache.get(model);
    if (cached) {
      return requestedFields.filter(f => cached.has(f));
    }

    try {
      const response = await this.queuedRpc('/web/dataset/call_kw', {
        model,
        method: 'fields_get',
        args: [],
        kwargs: { attributes: ['string', 'type'] },
      });

      if (response.error) {
        console.warn(`[getValidFields] Failed for '${model}':`, response.error);
        return requestedFields;
      }

      const validFields = new Set(Object.keys(response.result || {}));
      this.modelFieldsCache.set(model, validFields);

      const valid = requestedFields.filter(f => validFields.has(f));
      const invalid = requestedFields.filter(f => !validFields.has(f));
      if (invalid.length > 0) {
        console.warn(`[getValidFields] Model '${model}' missing fields:`, invalid);
      }
      return valid;
    } catch (error) {
      console.warn(`[getValidFields] Error for '${model}':`, error);
      return requestedFields;
    }
  }

  /**
   * Safe search and read - discovers valid fields via fields_get on first call,
   * then filters out invalid fields automatically. Falls back to retry on error.
   */
  async safeSearchRead(
    model: string,
    domain: any[],
    fields: string[],
    limit?: number,
    offset?: number,
    order?: string
  ): Promise<any[]> {
    try {
      const validFields = await this.getValidFields(model, fields);
      if (validFields.length === 0) return [];
      return await this.searchRead(model, domain, validFields, limit, offset, order);
    } catch (error: any) {
      const msg = error.message || '';
      const fieldMatch = msg.match(/Invalid field '(\w+)' on model '(\w+[\.\w]*)'/);
      if (fieldMatch) {
        const invalidField = fieldMatch[1];
        console.warn(`[safeSearchRead] Field '${invalidField}' not found on model '${model}', retrying without it`);
        const cached = this.modelFieldsCache.get(model);
        if (cached) cached.delete(invalidField);
        const filteredFields = fields.filter(f => f !== invalidField);
        if (filteredFields.length === 0) return [];
        return this.safeSearchRead(model, domain, filteredFields, limit, offset, order);
      }
      if (msg.includes('does not exist') || msg.includes('not found')) {
        console.warn(`[safeSearchRead] Model '${model}' not found, returning empty`);
        return [];
      }
      if (msg.includes('AccessError') || msg.includes('not allowed') || msg.includes('\u0644\u0627 \u064a\u064f\u0633\u0645\u062d') || msg.includes('\u0635\u0644\u0627\u062d\u064a\u0627\u062a')) {
        console.warn(`[safeSearchRead] Access denied for model '${model}', returning empty`);
        return [];
      }
      throw error;
    }
  }

  /**
   * Search for record IDs - with concurrency control
   */
  async search(
    model: string,
    domain: any[][],
    limit?: number,
    offset?: number,
    order?: string
  ): Promise<number[]> {
    const response = await this.queuedRpc('/web/dataset/call_kw', {
      model,
      method: 'search',
      args: [domain],
      kwargs: {
        limit: limit || 80,
        offset: offset || 0,
        order: order || '',
      },
    });

    if (response.error) {
      throw new Error(response.error.data?.message || 'Search failed');
    }

    return response.result || [];
  }

  /**
   * Get search count matching domain - with caching
   */
  async searchCount(model: string, domain: any[]): Promise<number> {
    const key = `count:${model}:${JSON.stringify(domain)}`;
    const cached = this.getCached(key);
    if (cached !== null) return cached;

    const response = await this.queuedRpc('/web/dataset/call_kw', {
      model,
      method: 'search_count',
      args: [domain],
      kwargs: {},
    });

    if (response.error) {
      throw new Error(response.error.data?.message || 'Search count failed');
    }

    const result = response.result || 0;
    this.setCache(key, result, this.defaultCacheTTL);
    return result;
  }

  /**
   * Create a new record - with cache invalidation
   */
  async create(model: string, values: Record<string, any>): Promise<number> {
    const response = await this.queuedRpc('/web/dataset/call_kw', {
      model,
      method: 'create',
      args: [values],
      kwargs: {},
    });

    if (response.error) {
      throw new Error(response.error.data?.message || 'Create failed');
    }

    this.invalidateModelCache(model);
    return response.result;
  }

  /**
   * Write (update) existing records - with cache invalidation
   */
  async write(model: string, ids: number[], values: Record<string, any>): Promise<boolean> {
    const response = await this.queuedRpc('/web/dataset/call_kw', {
      model,
      method: 'write',
      args: [ids, values],
      kwargs: {},
    });

    if (response.error) {
      throw new Error(response.error.data?.message || 'Write failed');
    }

    this.invalidateModelCache(model);
    return response.result;
  }

  /**
   * Read specific records by IDs - with caching
   */
  async read(model: string, ids: number[], fields: string[]): Promise<any[]> {
    const key = `read:${model}:${ids.join(',')}:${fields.join(',')}`;
    const cached = this.getCached(key);
    if (cached) return cached;

    const response = await this.queuedRpc('/web/dataset/call_kw', {
      model,
      method: 'read',
      args: [ids],
      kwargs: { fields },
    });

    if (response.error) {
      throw new Error(response.error.data?.message || 'Read failed');
    }

    const result = response.result || [];
    this.setCache(key, result, this.defaultCacheTTL);
    return result;
  }

  /**
   * Safe read - automatically filters out invalid fields using fields_get discovery.
   * Prevents errors when custom fields don't exist in Odoo.
   */
  async safeRead(model: string, ids: number[], fields: string[]): Promise<any[]> {
    const validFields = await this.getValidFields(model, fields);
    if (validFields.length === 0) return [];
    try {
      return await this.read(model, ids, validFields);
    } catch (error: any) {
      const msg = error.message || '';
      const fieldMatch = msg.match(/Invalid field '(\w+)' on model '(\w+[\.\w]*)'/);
      if (fieldMatch) {
        const invalidField = fieldMatch[1];
        const cached = this.modelFieldsCache.get(model);
        if (cached) cached.delete(invalidField);
        const filteredFields = validFields.filter(f => f !== invalidField);
        if (filteredFields.length === 0) return [];
        return this.read(model, ids, filteredFields);
      }
      throw error;
    }
  }

  /**
   * Get direct proxy URL for an attachment
   */
  getAttachmentUrl(attachmentId: number): string {
    if (!attachmentId) return '';
    const baseUrl = this.apiUrl.replace(/\/+$/, '');
    return `${baseUrl}/web/content/${attachmentId}?session_id=${encodeURIComponent(this.sessionId)}&odoo_url=${encodeURIComponent(this.url)}`;
  }

  /**
   * Get direct proxy URL for an image (allows Odoo resize parameters)
   */
  getImageUrl(attachmentId: number, width?: number, height?: number): string {
    if (!attachmentId) return '';
    const baseUrl = this.apiUrl.replace(/\/+$/, '');
    let url = `${baseUrl}/web/image/${attachmentId}?session_id=${encodeURIComponent(this.sessionId)}&odoo_url=${encodeURIComponent(this.url)}`;
    if (width) url += `&width=${width}`;
    if (height) url += `&height=${height}`;
    return url;
  }

  /**
   * Unlink (delete) records - with cache invalidation
   */
  async unlink(model: string, ids: number[]): Promise<boolean> {
    const response = await this.queuedRpc('/web/dataset/call_kw', {
      model,
      method: 'unlink',
      args: [ids],
      kwargs: {},
    });

    if (response.error) {
      throw new Error(response.error.data?.message || 'Unlink failed');
    }

    this.invalidateModelCache(model);
    return response.result;
  }

  // Helper to search a model by name and get ID
  async resolveNameToId(model: string, nameField: string, value: any): Promise<number | null> {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    const name = String(value).trim();
    if (!name) return null;

    try {
      // 1. Try exact match
      let results = await this.searchRead(model, [[nameField, '=', name]], ['id'], 1);
      if (results && results.length > 0) {
        return results[0].id;
      }
      // 2. Try partial match
      results = await this.searchRead(model, [[nameField, 'ilike', name]], ['id'], 1);
      if (results && results.length > 0) {
        return results[0].id;
      }
    } catch (e) {
      console.error(`Error resolving name to ID for ${model}:`, e);
    }
    return null;
  }

  /**
   * Find or create a partner (res.partner)
   * Searches by phone first (highest priority), then by name
   */
  async findOrCreatePartner(data: { name: string; phone?: string; street?: string }): Promise<number> {
    if (!data.name) return 0;

    // Search by phone first
    if (data.phone) {
      const normalizedPhone = this.normalizePhone(data.phone);
      const phoneVariations = this.getPhoneVariations(normalizedPhone);

      for (const phoneVar of phoneVariations) {
        const partners = await this.searchRead(
          'res.partner',
          ['|', ['phone', '=', phoneVar], ['mobile', '=', phoneVar]],
          ['id'],
          1
        );
        if (partners && partners.length > 0) {
          return partners[0].id;
        }
      }
    }

    // Search by name
    const partners = await this.searchRead(
      'res.partner',
      [['name', '=', data.name]],
      ['id', 'name', 'phone', 'street'],
      1
    );

    if (partners && partners.length > 0) {
      return partners[0].id;
    }

    // Create new partner
    const partnerValues: Record<string, any> = {
      name: data.name,
      phone: data.phone || '',
      street: data.street || '',
    };

    return await this.create('res.partner', partnerValues);
  }

  /**
   * Create an imported helpdesk ticket using the same import logic as the reference app.
   * This keeps template fields, fixed/dynamic tags, related partner values, relation conversions,
   * and ticket name/description generation instead of reducing the payload to a small fixed set.
   */
  async createImportedTicket(
    importedData: Record<string, any>,
    template?: ImportMappingTemplate,
    selectedCompanyId?: number | null
  ): Promise<{ ticketId: number; partnerId?: number }> {
    const ticketData: Record<string, any> = { ...importedData };
    const originalData = (ticketData._original_data && typeof ticketData._original_data === 'object')
      ? { ...ticketData._original_data }
      : {};

    Object.keys(ticketData).forEach(key => {
      if (key.startsWith('_')) delete ticketData[key];
    });

    const originalCategoryName = ticketData.x_product_category?.toString();
    const originalSymptomName = ticketData.x_symptom_id?.toString();
    const originalDocTypeName = ticketData.x_doc_type?.toString();
    const originalPartnerName = ticketData.partner_id?.toString();

    if (selectedCompanyId) {
      ticketData.x_company_id = selectedCompanyId;
    } else if (!ticketData.x_company_id && template?.odooCompanyIds?.length) {
      ticketData.x_company_id = template.odooCompanyIds[0];
    }

    const tagNames = this.collectImportTagNames(ticketData, template);
    let ticketName = template?.ticketNameTemplate
      ? this.expandTemplate(template.ticketNameTemplate, { ...originalData, ...ticketData })
      : undefined;

    if (template?.descriptionTemplate) {
      ticketData.description = this.expandTemplate(template.descriptionTemplate, { ...originalData, ...ticketData });
    }

    let partnerId: number | undefined;
    if (ticketData.partner_id) {
      if (typeof ticketData.partner_id === 'number') {
        partnerId = ticketData.partner_id;
      } else {
        partnerId = await this.findOrCreateImportPartner(ticketData, template);
        if (partnerId) ticketData.partner_id = partnerId;
      }
    }

    await this.resolveImportedRelations(ticketData);

    if (ticketName) {
      ticketName = this.replaceOriginalRelationNames(ticketName, {
        originalCategoryName,
        originalSymptomName,
        originalDocTypeName,
        originalPartnerName,
        externalRef: ticketData.x_external_ref,
      });
      ticketData.name = this.expandTemplate(ticketName, { ...originalData, ...ticketData });
    } else if (!ticketData.name) {
      const externalRef = ticketData.x_external_ref?.toString() || '';
      ticketData.name = externalRef
        ? `${externalRef}${originalCategoryName ? ` - ${originalCategoryName}` : ''}`
        : `تذكرة جديدة - ${new Date().toISOString().slice(0, 19).replace('T', ' ')}`;
    }

    if (tagNames.length > 0) {
      const tagIds = await this.findOrCreateTagIds(tagNames);
      if (tagIds.length > 0) ticketData.tag_ids = [[6, 0, tagIds]];
    }

    this.removePartnerOnlyFields(ticketData);
    Object.keys(ticketData).forEach(key => {
      const value = ticketData[key];
      if (value === undefined || value === null || value === '') delete ticketData[key];
    });

    const ticketValues = await this.filterValidCreateValues('helpdesk.ticket', ticketData);
    const ticketId = await this.create('helpdesk.ticket', ticketValues);
    return { ticketId, partnerId };
  }

  async fetchTemplateApiRows(template: ImportMappingTemplate): Promise<Record<string, any>[]> {
    if (!template.apiBaseUrl) return [];

    const baseUrl = template.apiBaseUrl.replace(/\/+$/, '');
    const proxyBaseUrl = this.apiUrl.replace(/\/+$/, '');
    const apiDb = template.apiDatabase || this.db;
    const authPayload = {
      db: apiDb,
      login: template.apiUsername,
      password: template.apiPassword,
    };
    const createProxyHeaders = (sessionId?: string): Record<string, string> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Odoo-Url': baseUrl,
      };

      if (apiDb) headers['X-Odoo-Db'] = apiDb;
      if (sessionId) headers['X-Session-Id'] = sessionId;

      return headers;
    };

    let apiSessionId = '';
    if (template.apiUsername && template.apiPassword) {
      const authResponse = await fetch(`${proxyBaseUrl}/web/session/authenticate`, {
        method: 'POST',
        headers: createProxyHeaders(),
        credentials: 'include',
        body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params: authPayload, id: Date.now() }),
      });

      if (!authResponse.ok) throw new Error(`API auth failed: ${authResponse.statusText}`);
      const authData = await authResponse.json();
      if (authData.error) throw new Error(authData.error.data?.message || authData.error.message || 'API auth failed');
      apiSessionId = authResponse.headers.get('X-Session-Id') || authData.result?.session_id || '';
    }

    const fields = Object.keys(template.apiFieldMappings || {});
    const commonModels = ['helpdesk.ticket', 'project.task'];

    for (const model of commonModels) {
      try {
        const response = await fetch(`${proxyBaseUrl}/web/dataset/call_kw`, {
          method: 'POST',
          headers: createProxyHeaders(apiSessionId),
          credentials: 'include',
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'call',
            params: {
              model,
              method: 'search_read',
              args: [[]],
              kwargs: {
                fields: fields.length ? fields : undefined,
                limit: 500,
                order: 'id desc',
              },
            },
            id: Date.now(),
          }),
        });

        if (!response.ok) continue;
        const data = await response.json();
        if (data.error) continue;
        if (Array.isArray(data.result)) return data.result;
      } catch (error) {
        console.warn(`API import fetch failed for model ${model}:`, error);
      }
    }

    return [];
  }

  /**
   * Check which external refs already exist as tickets
   */
  async checkExistingTickets(externalRefs: string[]): Promise<string[]> {
    if (!externalRefs.length) return [];

    // Batch check - Odoo has limits on domain length
    const batchSize = 200;
    const existing: string[] = [];

    for (let i = 0; i < externalRefs.length; i += batchSize) {
      const batch = externalRefs.slice(i, i + batchSize);
      const tickets = await this.searchRead(
        'helpdesk.ticket',
        [['x_external_ref', 'in', batch]],
        ['x_external_ref'],
        batchSize
      );
      tickets.forEach((t: any) => {
        if (t.x_external_ref) existing.push(t.x_external_ref);
      });
    }

    return existing;
  }

  /**
   * Create a helpdesk ticket with partner auto-creation and relationship fields
   */
  async createTicket(ticketData: Record<string, any>): Promise<{ ticketId: number; partnerId?: number }> {
    // Resolve relation fields if they are string names
    const resolvedData = { ...ticketData };

    if (typeof resolvedData.x_company_id === 'string' && resolvedData.x_company_id) {
      resolvedData.x_company_id = await this.resolveNameToId('res.company', 'name', resolvedData.x_company_id);
    }
    if (typeof resolvedData.x_product_category === 'string' && resolvedData.x_product_category) {
      resolvedData.x_product_category = await this.resolveNameToId('x_product.category', 'x_name', resolvedData.x_product_category);
    }
    if (typeof resolvedData.x_brand_id === 'string' && resolvedData.x_brand_id) {
      resolvedData.x_brand_id = await this.resolveNameToId('x_brand', 'x_name', resolvedData.x_brand_id);
    }
    if (typeof resolvedData.x_doc_type === 'string' && resolvedData.x_doc_type) {
      resolvedData.x_doc_type = await this.resolveNameToId('x_service_type', 'x_name', resolvedData.x_doc_type);
    }
    if (typeof resolvedData.x_symptom_id === 'string' && resolvedData.x_symptom_id) {
      resolvedData.x_symptom_id = await this.resolveNameToId('x_product_symptoms', 'x_name', resolvedData.x_symptom_id);
    }
    if (typeof resolvedData.x_partner_city === 'string' && resolvedData.x_partner_city) {
      resolvedData.x_partner_city = await this.resolveNameToId('x_res.country.state.city', 'x_name', resolvedData.x_partner_city);
    }
    if (typeof resolvedData.x_product_id === 'string' && resolvedData.x_product_id) {
      resolvedData.x_product_id = await this.resolveNameToId('product.template', 'name', resolvedData.x_product_id);
    }

    let partnerId = resolvedData.partner_id || undefined;

    // Find or create partner
    if (!partnerId && resolvedData.customer_name) {
      partnerId = await this.findOrCreatePartner({
        name: resolvedData.customer_name,
        phone: resolvedData.phone,
        street: resolvedData.x_address,
      });
    }

    // Update ticketData values to use the resolved values
    ticketData = resolvedData;

    // Build ticket description
    const descriptionParts: string[] = [];
    if (ticketData.problem) descriptionParts.push(`مشكلة الجهاز: ${ticketData.problem}`);
    if (ticketData.product) descriptionParts.push(`المنتج: ${ticketData.product}`);
    if (ticketData.model) descriptionParts.push(`الموديل: ${ticketData.model}`);
    if (ticketData.actual_model) descriptionParts.push(`الموديل الفعلي: ${ticketData.actual_model}`);
    if (ticketData.sap_number) descriptionParts.push(`رقم الساب: ${ticketData.sap_number}`);
    if (ticketData.central) descriptionParts.push(`السنترال: ${ticketData.central}`);
    if (ticketData.create_date_str) descriptionParts.push(`تاريخ الإنشاء: ${ticketData.create_date_str}`);

    // Generate ticket name from template or default
    const ticketName = ticketData.ticket_name_template
      ? this.generateTicketName(ticketData.ticket_name_template, ticketData)
      : `${ticketData.x_external_ref} - صيانة ${ticketData.product || ''}`;

    const ticketValues: Record<string, any> = {
      name: ticketName,
      x_external_ref: ticketData.x_external_ref,
      description: descriptionParts.join('\n'),
      x_address: ticketData.x_address || '',
      partner_id: partnerId || false,
      ticket_ref: ticketData.sap_number || false,
    };

    // Add optional fields
    if (ticketData.priority) ticketValues.priority = ticketData.priority;
    if (ticketData.team_id) ticketValues.team_id = ticketData.team_id;
    if (ticketData.company_id) ticketValues.company_id = ticketData.company_id;
    if (ticketData.x_company_id) ticketValues.x_company_id = ticketData.x_company_id;

    // Add relation fields
    if (ticketData.x_product_category) ticketValues.x_product_category = ticketData.x_product_category;
    if (ticketData.x_product_id) ticketValues.x_product_id = ticketData.x_product_id;
    if (ticketData.x_brand_id) ticketValues.x_brand_id = ticketData.x_brand_id;
    if (ticketData.x_doc_type) ticketValues.x_doc_type = ticketData.x_doc_type;
    if (ticketData.x_symptom_id) ticketValues.x_symptom_id = ticketData.x_symptom_id;
    if (ticketData.x_partner_city) ticketValues.x_partner_city = ticketData.x_partner_city;
    if (ticketData.x_ticket_note) ticketValues.x_ticket_note = ticketData.x_ticket_note;
    if (ticketData.x_product_serial) ticketValues.x_product_serial = ticketData.x_product_serial;

    const ticketId = await this.create('helpdesk.ticket', ticketValues);

    return { ticketId, partnerId };
  }

  /**
   * Logout and clear session
   */
  logout(): void {
    this.sessionId = '';
    this.uid = null;
    this.clearSession();
  }

  // ============ Private Methods ============

  /**
   * Make RPC call to Odoo via Worker proxy
   */
  async rpc(path: string, params: Record<string, any>): Promise<OdooJsonRpcResponse & { _sessionId?: string }> {
    rpcId++;
    const fullUrl = `${this.apiUrl}${path}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Send Odoo URL and session via custom headers to Worker
    if (this.url) {
      headers['X-Odoo-Url'] = this.url;
    }
    if (this.sessionId) {
      headers['X-Session-Id'] = this.sessionId;
    }
    if (this.db) {
      headers['X-Odoo-Db'] = this.db;
    }

    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'call',
          params,
          id: rpcId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Capture session from Worker response header
      const newSessionId = response.headers.get('X-Session-Id');
      if (newSessionId) {
        this.sessionId = newSessionId;
        data._sessionId = newSessionId;
      }

      // Check for session expiry
      if (data.error?.code === 100) {
        this.clearSession();
        throw new Error('انتهت الجلسة. سجل دخولك مجدداً');
      }

      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('خطأ في الاتصال. تحقق من الشبكة');
      }
      throw error;
    }
  }

  /**
   * Normalize phone number for matching
   */
  private normalizePhone(phone: string): string {
    let normalized = phone.replace(/[\s\-\(\)]/g, '');
    if (normalized.startsWith('+20')) {
      normalized = '0' + normalized.substring(3);
    } else if (normalized.startsWith('0020')) {
      normalized = '0' + normalized.substring(4);
    } else if (normalized.startsWith('20') && normalized.length > 10) {
      normalized = '0' + normalized.substring(2);
    }
    if (!normalized.startsWith('0') && normalized.length === 10) {
      normalized = '0' + normalized;
    }
    return normalized;
  }

  /**
   * Get phone number variations for search
   */
  private getPhoneVariations(phone: string): string[] {
    const variations = new Set<string>([phone]);
    if (phone.startsWith('0')) {
      const withoutZero = phone.substring(1);
      variations.add(withoutZero);
      variations.add('+20' + withoutZero);
      variations.add('20' + withoutZero);
      variations.add('0020' + withoutZero);
    }
    return Array.from(variations);
  }

  private collectImportTagNames(ticketData: Record<string, any>, template?: ImportMappingTemplate): string[] {
    const tagNames = new Set<string>();
    template?.fixedTags?.forEach(tag => {
      if (tag?.trim()) tagNames.add(tag.trim());
    });
    Object.entries(template?.dynamicTags || {}).forEach(([fieldName, tagPrefix]) => {
      const fieldValue = ticketData[fieldName];
      if (fieldValue !== undefined && fieldValue !== null && fieldValue.toString().trim()) {
        tagNames.add(`${tagPrefix || ''}${fieldValue.toString().trim()}`);
      }
    });
    if (typeof ticketData.x_tags === 'string') {
      ticketData.x_tags.split(',').map((v: string) => v.trim()).filter(Boolean).forEach((tag: string) => tagNames.add(tag));
    }
    return Array.from(tagNames);
  }

  private async findOrCreateImportPartner(ticketData: Record<string, any>, template?: ImportMappingTemplate): Promise<number | undefined> {
    const partnerName = ticketData.partner_id?.toString().trim();
    if (!partnerName) return undefined;

    const partnerPhone = ticketData.partner_phone || ticketData.phone;
    const partnerMobile = ticketData.partner_mobile || ticketData.mobile;
    const normalizedPhone = partnerPhone ? this.normalizePhone(partnerPhone.toString()) : undefined;
    const normalizedMobile = partnerMobile ? this.normalizePhone(partnerMobile.toString()) : undefined;

    for (const phone of [normalizedPhone, normalizedMobile].filter(Boolean) as string[]) {
      const phoneKey = phone.startsWith('0') ? phone.substring(1) : phone;
      const partners = await this.safeSearchRead(
        'res.partner',
        ['|', '|', '|', ['phone', 'in', this.getPhoneVariations(phone)], ['mobile', 'in', this.getPhoneVariations(phone)], ['phone', 'ilike', phoneKey], ['mobile', 'ilike', phoneKey]],
        ['id', 'name', 'phone', 'mobile'],
        1
      );
      if (partners.length > 0) return partners[0].id;
    }

    const partnerData: Record<string, any> = {
      name: partnerName,
      phone: normalizedPhone || partnerPhone || '',
      mobile: normalizedMobile || partnerMobile || '',
      street: ticketData.street || ticketData.x_address || '',
    };

    if (ticketData.x_phone2) partnerData.x_phone2 = ticketData.x_phone2;
    if (ticketData.state_id) partnerData.state_id = await this.resolveStateValue(ticketData.state_id);
    if (ticketData.x_city_id) partnerData.x_city_id = await this.resolveCityValue(ticketData.x_city_id);
    if (ticketData.x_partner_city && !partnerData.x_city_id) partnerData.x_city_id = await this.resolveCityValue(ticketData.x_partner_city);

    const relatedValues = template?.relatedFieldValues?.partner_id || {};
    for (const [field, value] of Object.entries(relatedValues)) {
      if (field === 'state_id') partnerData[field] = await this.resolveStateValue(value);
      else if (field === 'x_city_id') partnerData[field] = await this.resolveCityValue(value);
      else partnerData[field] = value;
    }

    Object.keys(partnerData).forEach(key => {
      const value = partnerData[key];
      if (value === undefined || value === null || value === '') delete partnerData[key];
    });

    return await this.create('res.partner', await this.filterValidCreateValues('res.partner', partnerData));
  }

  private async resolveImportedRelations(ticketData: Record<string, any>): Promise<void> {
    const relationResolvers: Record<string, () => Promise<number | null>> = {
      x_company_id: () => this.resolveNameToId('res.company', 'name', ticketData.x_company_id),
      x_product_category: () => this.resolveNameToId('x_product.category', 'x_name', ticketData.x_product_category),
      x_brand_id: () => this.resolveNameToId('x_brand', 'x_name', ticketData.x_brand_id),
      x_doc_type: () => this.resolveNameToId('x_service_type', 'x_name', ticketData.x_doc_type),
      x_symptom_id: () => this.resolveNameToId('x_product_symptoms', 'x_name', ticketData.x_symptom_id),
      x_partner_city: () => this.resolveNameToId('x_res.country.state.city', 'x_name', ticketData.x_partner_city),
      x_city_id: () => this.resolveNameToId('x_res.country.state.city', 'x_name', ticketData.x_city_id),
      x_product_id: () => this.resolveNameToId('product.template', 'name', ticketData.x_product_id),
    };

    for (const [field, resolver] of Object.entries(relationResolvers)) {
      if (typeof ticketData[field] !== 'string' || !ticketData[field].trim()) continue;
      const resolved = await resolver();
      if (resolved) ticketData[field] = resolved;
      else if (['x_product_category', 'x_symptom_id', 'x_doc_type'].includes(field)) delete ticketData[field];
    }
  }

  private async resolveStateValue(value: any): Promise<any> {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) return await this.resolveNameToId('res.country.state', 'name', value);
    return value;
  }

  private async resolveCityValue(value: any): Promise<any> {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) return await this.resolveNameToId('x_res.country.state.city', 'x_name', value);
    return value;
  }

  private async findOrCreateTagIds(tagNames: string[]): Promise<number[]> {
    const tagIds: number[] = [];
    for (const tagName of tagNames) {
      const name = tagName.trim();
      if (!name) continue;
      const existingTags = await this.safeSearchRead('helpdesk.tag', [['name', '=', name]], ['id', 'name'], 1);
      if (existingTags.length > 0) {
        tagIds.push(existingTags[0].id);
        continue;
      }
      try {
        tagIds.push(await this.create('helpdesk.tag', { name }));
      } catch (e) {
        console.warn(`Could not create helpdesk tag "${name}"`, e);
      }
    }
    return tagIds;
  }

  private async filterValidCreateValues(model: string, values: Record<string, any>): Promise<Record<string, any>> {
    const validFields = await this.getValidFields(model, Object.keys(values));
    return validFields.reduce<Record<string, any>>((acc, field) => {
      acc[field] = values[field];
      return acc;
    }, {});
  }

  private removePartnerOnlyFields(ticketData: Record<string, any>): void {
    ['partner_phone', 'partner_mobile', 'phone', 'mobile', 'x_phone2', 'street', 'city', 'x_city_id', 'state_id', 'x_tags']
      .forEach(field => delete ticketData[field]);
  }

  private replaceOriginalRelationNames(template: string, originals: { originalCategoryName?: string; originalSymptomName?: string; originalDocTypeName?: string; originalPartnerName?: string; externalRef?: any }): string {
    let result = template;
    const docType = originals.originalDocTypeName || '';
    const shortDocType = docType.includes('تركيب') ? 'تركيب' : docType.includes('صيانة') ? 'صيانة' : docType.includes('استبدال') ? 'استبدال' : docType;
    const replacements: Record<string, any> = {
      x_product_category: originals.originalCategoryName,
      x_category_id: originals.originalCategoryName,
      x_symptom_id: originals.originalSymptomName,
      x_doc_type: originals.originalDocTypeName,
      x_doc_type_short: shortDocType,
      partner_id: originals.originalPartnerName,
      'Call No.': originals.externalRef,
      'Call No': originals.externalRef,
      x_external_ref: originals.externalRef,
    };
    Object.entries(replacements).forEach(([key, value]) => {
      if (value !== undefined && value !== null) result = result.replace(new RegExp(`\\{${this.escapeRegExp(key)}\\}`, 'g'), String(value));
    });
    return result;
  }

  /**
   * Generate ticket name from template
   */
  private generateTicketName(template: string, data: Record<string, any>): string {
    return this.expandTemplate(template, data);
  }

  private expandTemplate(template: string, data: Record<string, any>): string {
    let result = template;
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        result = result.replace(new RegExp(`\\{${this.escapeRegExp(key)}\\}`, 'g'), String(value));
      }
    });
    return result;
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private saveSession(session: OdooSession): void {
    try {
      localStorage.setItem('odoo_session', JSON.stringify(session));
      localStorage.setItem('odoo_session_time', String(this.sessionStartTime || Date.now()));
    } catch {
      // localStorage might be unavailable
    }
  }

  private saveCredentials(credentials: { url: string; db: string }): void {
    try {
      localStorage.setItem('odoo_credentials', JSON.stringify(credentials));
    } catch {
      // localStorage might be unavailable
    }
  }

  private loadSession(): OdooSession | null {
    try {
      const saved = localStorage.getItem('odoo_session');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }

  private loadCredentials(): { url: string; db: string } | null {
    try {
      const saved = localStorage.getItem('odoo_credentials');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }

  private clearSession(): void {
    try {
      localStorage.removeItem('odoo_session');
      localStorage.removeItem('odoo_credentials');
      localStorage.removeItem('odoo_session_time');
    } catch {
      // localStorage might be unavailable
    }
  }
}

// Singleton instance
import { env } from '@/config/env';
export const odooClient = new OdooClient(env.apiUrl);
